import { formatCents } from '@nexus/shared';

/**
 * How numbers, money and instants are written, in one place.
 *
 * THREE RULES THIS FILE EXISTS TO KEEP.
 *
 * 1. A number a person compares down a column is tabular. Money and view
 *    counts sit in a `.num` span so the digits line up row to row; a column of
 *    proportional figures is a column nobody can scan.
 *
 * 2. `null` is not zero. A platform that published no view count is a dash and
 *    a sentence, never `0` — an Instagram photo has no play count and a
 *    YouTube owner can hide theirs, and "0" tells a creator their clip
 *    collapsed.
 *
 * 3. A date is written the way a person says it, with the exact instant on
 *    hover. `2026-09-21T10:04:00.000Z` is a machine's answer to "when";
 *    "21 Sep 2026" is a person's. The `<time>` element keeps the machine one
 *    in `dateTime` and the full stamp in `title`, so nothing is lost by making
 *    it readable.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const FULL_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const pad = (n: number): string => String(n).padStart(2, '0');

/** `21 Sep 2026`, or `21 Sep 2026, 10:04` when the time matters. */
export function formatWhen(iso: string, withTime = false): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const date = `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;
  return withTime ? `${date}, ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}` : date;
}

/**
 * `Sep 2026` — a MONTH, not a day in one.
 *
 * A month bucket on the earnings chart labelled "1 Sep 2026" reads as one day's
 * earnings, which is the opposite of what it is. Its own function rather than a
 * slice of `formatWhen`, because that string starts with the day-of-month and
 * cutting a fixed number of characters off it gives "an 2026" for the 1st.
 */
export function formatMonth(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;
}

/** What the tooltip says: the whole instant, named as UTC so it is unambiguous. */
export function exactWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return (
    `${DAYS[at.getUTCDay()]} ${at.getUTCDate()} ${FULL_MONTHS[at.getUTCMonth()]} ` +
    `${at.getUTCFullYear()} at ${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}:` +
    `${pad(at.getUTCSeconds())} UTC`
  );
}

export function When({
  value,
  withTime = false,
  fallback = '—',
  className,
}: {
  value: string | null | undefined;
  withTime?: boolean;
  fallback?: string;
  className?: string;
}): React.JSX.Element {
  if (!value) return <span className={`muted ${className ?? ''}`.trim()}>{fallback}</span>;
  return (
    <time dateTime={value} title={exactWhen(value)} className={className}>
      {formatWhen(value, withTime)}
    </time>
  );
}

export function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

/** A count. `null` means the platform published none of it, and says so. */
export function Num({
  value,
  fallback = '—',
  suffix,
}: {
  value: number | null | undefined;
  fallback?: string;
  suffix?: string;
}): React.JSX.Element {
  if (value === null || value === undefined) return <span className="muted">{fallback}</span>;
  return (
    <span className="num">
      {formatNumber(value)}
      {suffix ? ` ${suffix}` : ''}
    </span>
  );
}

/** Money, always from integer cents, always tabular. */
export function Money({
  cents,
  fallback = '—',
}: {
  cents: number | null | undefined;
  fallback?: string;
}): React.JSX.Element {
  if (cents === null || cents === undefined) return <span className="muted">{fallback}</span>;
  return <span className="num">{formatCents(cents)}</span>;
}

/**
 * An outward link, opened in a new tab and wrapped rather than truncated.
 *
 * NOTHING IN THIS APP IS CUT OFF WITH AN ELLIPSIS. A half-shown post URL is
 * the one thing a creator wants to copy when they are asking why a submission
 * was refused.
 */
export function ExternalLink({
  href,
  children,
  className,
}: {
  href: string;
  children?: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={`break ${className ?? ''}`.trim()}
    >
      {children ?? href}
    </a>
  );
}
